import { useState, useEffect, useRef } from 'react'
import { useAuthStore } from '../store/useAuthStore'
import { useI18n } from '../i18n'

interface Props {
  value: string
  onChange: (v: string) => void
}

export function UsernameField({ value, onChange }: Props) {
  const { t } = useI18n()
  const checkUsername = useAuthStore((s) => s.checkUsername)
  const usernameStatus = useAuthStore((s) => s.usernameStatus)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!touched) return
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      checkUsername(value)
    }, 500)
    return () => clearTimeout(timerRef.current)
  }, [value, touched, checkUsername])

  const borderColor =
    !touched || usernameStatus === 'idle' || usernameStatus === 'checking'
      ? 'border-surface-lighter'
      : usernameStatus === 'available'
        ? 'border-success'
        : 'border-error'

  return (
    <div>
      <input
        type="text"
        maxLength={10}
        placeholder={t('auth.username')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        className={`w-full rounded-lg border-2 ${borderColor} bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary`}
      />
      {touched && usernameStatus !== 'idle' && (
        <p
          className={`mt-1 text-xs ${
            usernameStatus === 'available' ? 'text-success' :
            usernameStatus === 'checking' ? 'text-text-muted' : 'text-error'
          }`}
        >
          {usernameStatus === 'checking' && t('auth.usernameChecking')}
          {usernameStatus === 'available' && t('auth.usernameAvailable')}
          {usernameStatus === 'taken' && t('auth.usernameTaken')}
          {usernameStatus === 'invalid' && t('auth.usernameInvalid')}
        </p>
      )}
    </div>
  )
}
